import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    makeStyles,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core"
import { Print } from "@material-ui/icons"
import { useEffect, useState } from "react"
import { buscarInformacoesPedido } from "../../services/integracao"
import useAppStore from "../../stores/AppStore"
import { corStatusPill, formatarMoeda, tipoPill } from "../HistoricoPedidos/constants"

const useStyles = makeStyles(theme => ({
    tituloLinha: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
    },
    acoes: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "0.5rem",
        marginBottom: 16,
        "& > button": { color: "white" },
        [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    item: {
        padding: "8px 0",
        "&:not(:last-child)": { borderBottom: "1px solid #eee" },
    },
    itemTopo: {
        display: "flex",
        justifyContent: "space-between",
        gap: 8,
    },
    subItem: {
        color: "#555",
        marginLeft: 16,
        display: "flex",
        justifyContent: "space-between",
        gap: 8,
    },
    grupoOpcao: {
        marginLeft: 16,
        color: "#888",
        fontWeight: 600,
    },
    obs: {
        marginLeft: 16,
        color: "#b26a00",
        fontStyle: "italic",
    },
    categoria: {
        color: "#888",
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },
    totalLinha: {
        display: "flex",
        justifyContent: "space-between",
        padding: "2px 0",
    },
    carregando: { textAlign: "center", padding: 40 },
}))

// Cores dos botões de status, espelhando o diálogo do Recebedor de Pedidos.
const CORES_ACAO = {
    aceitar: "#28a745",
    preparar: "#fd7e14",
    despachar: "#0080ff",
    finalizar: "#28a745",
    cancelar: "#dc3545",
}

const Info = ({ rotulo, valor }) =>
    valor ? (
        <Box>
            <Typography variant="caption" color="textSecondary">
                {rotulo}
            </Typography>
            <Typography variant="body2">{valor}</Typography>
        </Box>
    ) : null

/**
 * Visualização amigável do pedido, com os itens (adicionais e observações) e as
 * opções de trocar o status, imprimir ou fechar — similar ao Recebedor.
 *
 * Os detalhes completos (itens, endereço, pagamento) são buscados sob demanda ao
 * abrir, pois a listagem da tela não traz esses campos.
 */
export default function DialogVerPedido({ pedido, acoes, autorizacao, onAcao, onClose }) {
    const classes = useStyles()
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("xs"))
    const mandarImprimir = useAppStore(s => s.mandarImprimir)

    const [detalhe, setDetalhe] = useState(null)
    const [carregando, setCarregando] = useState(false)

    const aberto = !!pedido

    useEffect(() => {
        if (!aberto || !pedido?.id) return
        let cancelado = false
        setDetalhe(null)
        setCarregando(true)
        buscarInformacoesPedido(autorizacao, pedido.id).then(info => {
            if (cancelado) return
            setCarregando(false)
            if (info?.id) setDetalhe(info)
        })
        return () => {
            cancelado = true
        }
    }, [aberto, pedido?.id, autorizacao])

    if (!aberto) return null

    // Usa os detalhes completos quando disponíveis; cai para o pedido da listagem.
    const dados = detalhe || pedido
    const pillTipo = tipoPill(dados.type)
    const pillStatus = corStatusPill(dados.descricaoStatus)

    const endereco = dados.deliveryAddress
    const enderecoTexto = endereco
        ? [
              [endereco.street, endereco.streetNumber].filter(Boolean).join(", "),
              endereco.neighborhood,
              endereco.city,
          ]
              .filter(Boolean)
              .join(" - ")
        : ""

    const pagamento = Array.isArray(dados.payments)
        ? dados.payments.map(p => p.name).join(", ")
        : dados.payments?.name

    const desconto = dados.discountOrder ?? dados.discountTotal
    const itens = dados.items || []

    const imprimir = () => {
        mandarImprimir({
            pedido: dados,
            texto: dados.texto,
            qtd: 1,
            imprimirNota: true,
            reimpressaoManual: true,
        })
    }

    return (
        <Dialog open={aberto} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography>
                <div className={classes.tituloLinha}>
                    <Typography variant="h6">
                        Pedido #{dados.id}
                        <Chip
                            label={dados.descricaoStatus || "—"}
                            size="small"
                            style={{
                                background: pillStatus.bg,
                                color: pillStatus.color,
                                marginLeft: 8,
                            }}
                        />
                    </Typography>
                    <Typography variant="h6">{formatarMoeda(dados.totalPrice)}</Typography>
                </div>
            </DialogTitle>

            <DialogContent dividers>
                {acoes.length > 0 && (
                    <div className={classes.acoes}>
                        {acoes.map(a => (
                            <Button
                                key={a.tipo}
                                fullWidth
                                variant="contained"
                                style={{ backgroundColor: CORES_ACAO[a.tipo] || "#607d8b" }}
                                onClick={() => {
                                    onAcao(pedido, a.tipo)
                                    onClose()
                                }}
                            >
                                {a.label}
                            </Button>
                        ))}
                    </div>
                )}

                {carregando ? (
                    <div className={classes.carregando}>
                        <CircularProgress size={28} />
                    </div>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Info rotulo="Data/Hora" valor={dados.dateFormatted} />
                            </Grid>
                            <Grid item xs={6}>
                                <Info rotulo="Tipo" valor={pillTipo.label} />
                            </Grid>
                            <Grid item xs={6}>
                                <Info rotulo="Pagamento" valor={pagamento} />
                            </Grid>
                            <Grid item xs={6}>
                                <Info rotulo="Cliente" valor={dados.customer?.name} />
                            </Grid>
                            {dados.customer?.phone && (
                                <Grid item xs={6}>
                                    <Info rotulo="Telefone" valor={dados.customer.phone} />
                                </Grid>
                            )}
                            {enderecoTexto && (
                                <Grid item xs={12}>
                                    <Info rotulo="Endereço de entrega" valor={enderecoTexto} />
                                </Grid>
                            )}
                        </Grid>

                        <Box mt={2} mb={1}>
                            <Typography variant="subtitle2">Itens</Typography>
                        </Box>
                        <Divider />

                        {itens.length === 0 && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ padding: "12px 0" }}
                            >
                                Sem itens.
                            </Typography>
                        )}

                        {itens.map((item, i) => (
                            <div className={classes.item} key={i}>
                                {item.nameCategory && (
                                    <Typography variant="caption" className={classes.categoria}>
                                        {item.nameCategory}
                                    </Typography>
                                )}
                                <div className={classes.itemTopo}>
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                        {Math.round(item.quantity)}x {item.name}
                                    </Typography>
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                        {formatarMoeda(item.totalPrice)}
                                    </Typography>
                                </div>

                                {(item.subItems || []).map((sub, j) => (
                                    <div key={j}>
                                        {sub.nomeGrupoOpcoes && (
                                            <Typography
                                                variant="caption"
                                                className={classes.grupoOpcao}
                                            >
                                                {sub.nomeGrupoOpcoes}
                                            </Typography>
                                        )}
                                        <div className={classes.subItem}>
                                            <Typography variant="body2">
                                                › {Math.round(sub.quantity)}x {sub.name}
                                            </Typography>
                                            {sub.totalPrice > 0 && (
                                                <Typography variant="body2">
                                                    {formatarMoeda(sub.totalPrice)}
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {item.observations?.trim() && (
                                    <Typography variant="body2" className={classes.obs}>
                                        Obs.: {item.observations.trim()}
                                    </Typography>
                                )}
                            </div>
                        ))}

                        {dados.observations?.trim() && (
                            <Box mt={2}>
                                <Typography variant="caption" color="textSecondary">
                                    Observação do pedido
                                </Typography>
                                <Typography variant="body2" className={classes.obs}>
                                    {dados.observations.trim()}
                                </Typography>
                            </Box>
                        )}

                        <Box mt={2}>
                            <Divider />
                            <Box mt={1}>
                                {dados.subTotal != null && (
                                    <div className={classes.totalLinha}>
                                        <Typography variant="body2" color="textSecondary">
                                            Subtotal
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatarMoeda(dados.subTotal)}
                                        </Typography>
                                    </div>
                                )}
                                {dados.deliveryFee != null && (
                                    <div className={classes.totalLinha}>
                                        <Typography variant="body2" color="textSecondary">
                                            Taxa de entrega
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatarMoeda(dados.deliveryFee)}
                                        </Typography>
                                    </div>
                                )}
                                {desconto > 0 && (
                                    <div className={classes.totalLinha}>
                                        <Typography variant="body2" color="textSecondary">
                                            Desconto
                                        </Typography>
                                        <Typography variant="body2">
                                            - {formatarMoeda(desconto)}
                                        </Typography>
                                    </div>
                                )}
                                {dados.taxaServico > 0 && (
                                    <div className={classes.totalLinha}>
                                        <Typography variant="body2" color="textSecondary">
                                            Taxa adicional
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatarMoeda(dados.taxaServico)}
                                        </Typography>
                                    </div>
                                )}
                                <div className={classes.totalLinha}>
                                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                        Total
                                    </Typography>
                                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                        {formatarMoeda(dados.totalPrice)}
                                    </Typography>
                                </div>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    startIcon={<Print />}
                    onClick={imprimir}
                    disabled={carregando || !detalhe}
                >
                    Imprimir
                </Button>
                <Button variant="contained" onClick={onClose}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
